(() => {
  if (customElements.get('pr-image-banner')) return;

  class PrImageBanner extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToBanner) return;
      this.isConnectedToBanner = true;

      this.frame = this.querySelector('.pr-image-banner__frame');
      this.viewport = this.querySelector('[data-pr-banner-viewport]');
      this.track = this.querySelector('[data-pr-banner-track]');
      this.slides = Array.from(this.querySelectorAll('[data-pr-banner-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-pr-banner-dot]'));
      this.toggle = this.querySelector('[data-pr-banner-toggle]');
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.interval = Math.max(5000, Number(this.dataset.autoplayInterval) || 10000);
      this.userPaused = this.dataset.autoplay !== 'true';
      this.interactionPaused = false;
      this.desktopQuery = window.matchMedia('(min-width: 769px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.reducedMotionQuery.matches) this.userPaused = true;

      this.onClick = this.handleClick.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onPointerEnter = this.handlePointerEnter.bind(this);
      this.onPointerLeave = this.handlePointerLeave.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onMediaChange = this.handleMediaChange.bind(this);

      this.slides.forEach((slide) => slide.removeAttribute('hidden'));
      this.show(this.index, { immediate: true });
      this.setAttribute('data-ready', '');
      this.updateToggle();

      this.addEventListener('click', this.onClick);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.desktopQuery.addEventListener('change', this.onMediaChange);

      if ('ResizeObserver' in window && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this);
      }

      this.scheduleAutoplay();
    }

    disconnectedCallback() {
      if (!this.isConnectedToBanner) return;
      this.isConnectedToBanner = false;
      this.clearAutoplay();
      this.resizeObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.desktopQuery?.removeEventListener('change', this.onMediaChange);
      this.removeAttribute('data-ready');
    }

    handleClick(event) {
      const control = event.target.closest('[data-pr-banner-prev], [data-pr-banner-next], [data-pr-banner-dot], [data-pr-banner-toggle]');
      if (!control || !this.contains(control) || this.slides.length < 2) return;

      if (control.hasAttribute('data-pr-banner-toggle')) {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.scheduleAutoplay();
        return;
      }

      if (control.hasAttribute('data-pr-banner-prev')) this.show(this.index - 1);
      else if (control.hasAttribute('data-pr-banner-next')) this.show(this.index + 1);
      else this.show(Number(control.dataset.prBannerDot));

      this.scheduleAutoplay();
    }

    handleBlockSelect(event) {
      const block = event.target.closest('[data-pr-banner-slide]');
      if (!block || !this.contains(block)) return;
      this.userPaused = true;
      this.updateToggle();
      this.show(this.slides.indexOf(block));
      this.clearAutoplay();
    }

    handlePointerEnter() {
      this.interactionPaused = true;
      this.clearAutoplay();
    }

    handlePointerLeave() {
      this.interactionPaused = false;
      this.scheduleAutoplay();
    }

    handleFocusIn() {
      this.interactionPaused = true;
      this.clearAutoplay();
    }

    handleFocusOut() {
      window.setTimeout(() => {
        this.interactionPaused = this.contains(document.activeElement);
        this.scheduleAutoplay();
      }, 0);
    }

    handleVisibilityChange() {
      if (document.hidden) this.clearAutoplay();
      else this.scheduleAutoplay();
    }

    handleMediaChange() {
      this.updateHeight();
    }

    show(nextIndex, { immediate = false } = {}) {
      if (!this.slides.length || !Number.isInteger(nextIndex)) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;

      if (immediate && this.track) this.track.style.transition = 'none';
      this.style.setProperty('--pr-banner-index', String(this.index));

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });

      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
      });

      this.updateHeight();

      if (immediate && this.track) {
        requestAnimationFrame(() => this.track.style.removeProperty('transition'));
      }
    }

    updateHeight() {
      if (!this.viewport || !this.frame || !this.slides[this.index]) return;
      const isDesktop = this.desktopQuery.matches;
      const mode = isDesktop ? this.frame.dataset.heightMode : this.frame.dataset.heightModeMobile;
      const autoHeight = this.frame.dataset.layout === 'overlay'
        && this.frame.dataset.imageFit === 'contain'
        && this.frame.dataset.fullscreen !== 'true'
        && mode === 'image';

      const slide = this.slides[this.index];
      const ratio = Number(isDesktop ? slide.dataset.desktopRatio : slide.dataset.mobileRatio) || 1.7778;
      this.viewport.style.setProperty('--pr-active-ratio', String(Number(slide.dataset.desktopRatio) || ratio));
      this.viewport.style.setProperty('--pr-active-ratio-mobile', String(Number(slide.dataset.mobileRatio) || ratio));

      if (!autoHeight) {
        this.viewport.style.removeProperty('height');
        return;
      }

      const width = this.viewport.clientWidth;
      if (width > 0) this.viewport.style.height = `${Math.round((width / ratio) * 100) / 100}px`;
    }

    updateToggle() {
      if (!this.toggle) return;
      const playing = !this.userPaused;
      this.toggle.dataset.state = playing ? 'playing' : 'paused';
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
    }

    scheduleAutoplay() {
      this.clearAutoplay();
      if (this.slides.length < 2 || this.userPaused || this.interactionPaused || document.hidden) return;
      this.autoplayTimer = window.setTimeout(() => {
        this.show(this.index + 1);
        this.scheduleAutoplay();
      }, this.interval);
    }

    clearAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('pr-image-banner', PrImageBanner);
})();
